/**
 * The cohort view.
 *
 * "A record page answers how one person is doing; this screen answers who is
 * blocking." Two rules from the book drive nearly everything here, and both are
 * easy to get subtly wrong:
 *
 *  - The blocking column names a PARTY, not a step, "because the useful action
 *    is a nudge to a person". Showing "Laptop issued" tells a reader what is
 *    missing; showing "Waiting on IT" tells them who to chase.
 *
 *  - "A joiner who has started and is still incomplete is red, not amber,
 *    however few steps remain — the payroll cut-off does not care how close
 *    they got." So lateness is decided by the start date, never by percentage.
 */

const DAY_MS = 86400000;

/** The parties a step can be owed by, in the order the book lists them. */
export const OWNERS = ["employee", "it", "admin", "manager"];

export const OWNER_LABEL = {
  employee: "New joiners",
  it: "IT",
  admin: "HR",
  manager: "Managers",
};

/** Singular, for a sentence about one person's block. */
export const OWNER_BLOCKING = {
  employee: "the joiner",
  it: "IT",
  admin: "HR",
  manager: "their manager",
};

/** The UTC calendar day of a value, as a number of days since the epoch. */
export const dayNumber = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  const t = m
    ? Date.parse(`${m[1]}-${m[2]}-${m[3]}T00:00:00.000Z`)
    : (() => {
        const d = new Date(s);
        return Number.isNaN(d.getTime())
          ? NaN
          : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      })();
  return Number.isNaN(t) ? null : Math.floor(t / DAY_MS);
};

export const todayNumber = (now = new Date()) =>
  Math.floor(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / DAY_MS);

/** Days until a joiner starts. Negative once they have started. */
export const daysUntilStart = (row, now) => {
  const start = dayNumber(row?.employee?.joiningDate);
  if (start == null) return null;
  return start - todayNumber(now);
};

const outstanding = (row) =>
  (row?.items || []).filter((i) => i.status === "pending" && i.required !== false);

/**
 * Whether this joiner is late, and how the row reads.
 *
 * `started` is the whole question: before the start date an incomplete
 * checklist is simply in progress; on or after it, it is blocking, whatever the
 * percentage says.
 */
export const joinerState = (row, now) => {
  const complete = Boolean(row?.progress?.complete);
  const until = daysUntilStart(row, now);
  const started = until != null && until <= 0;

  if (complete) return { key: "complete", label: "Complete", tone: "success" };
  if (started) {
    // Red, not amber, however few steps remain.
    return { key: "blocked", label: "Blocked", tone: "danger", started: true };
  }
  if (until != null && until <= 7) {
    return { key: "starting", label: "Starting soon", tone: "warning" };
  }
  if (!outstanding(row).length) return { key: "onTrack", label: "On track", tone: "info" };
  if ((row?.progress?.done || 0) === 0) {
    return { key: "notStarted", label: "Not started", tone: "muted" };
  }
  return { key: "inProgress", label: "In progress", tone: "info" };
};

/**
 * Who is holding this joiner up.
 *
 * The party owing the most outstanding required steps, with the soonest due
 * date breaking a tie — a nudge should go to whoever is furthest behind, and
 * where that is level, to whoever is late first.
 */
export const blockingParty = (row) => {
  const pending = outstanding(row);
  if (!pending.length) return null;

  const byOwner = new Map();
  pending.forEach((i) => {
    const owner = i.owner || "admin";
    const entry = byOwner.get(owner) || { owner, count: 0, soonest: null, labels: [] };
    entry.count += 1;
    entry.labels.push(i.label);
    const due = dayNumber(i.dueDate);
    if (due != null && (entry.soonest == null || due < entry.soonest)) entry.soonest = due;
    byOwner.set(owner, entry);
  });

  return [...byOwner.values()].sort(
    (a, b) =>
      b.count - a.count ||
      (a.soonest ?? Infinity) - (b.soonest ?? Infinity) ||
      OWNERS.indexOf(a.owner) - OWNERS.indexOf(b.owner)
  )[0];
};

/** The line under the blocking chip: what that party actually owes. */
export const blockingReason = (row) => {
  const party = blockingParty(row);
  if (!party) return row?.progress?.complete ? "Nothing outstanding" : "Waiting to start";
  const [first, ...rest] = party.labels;
  return rest.length ? `${first} and ${rest.length} more` : first;
};

/** The five counters. */
export const cohortStats = (rows = [], now) => {
  const live = rows.filter((r) => !r?.progress?.complete);
  const states = rows.map((r) => ({ row: r, state: joinerState(r, now) }));

  const blocked = states.filter((s) => s.state.key === "blocked");
  const startingThisWeek = rows.filter((r) => {
    const d = daysUntilStart(r, now);
    return d != null && d >= 0 && d <= 7;
  });

  const stepsOutstanding = live.reduce((t, r) => t + outstanding(r).length, 0);
  const stepsDue = live.reduce(
    (t, r) =>
      t +
      outstanding(r).filter((i) => {
        const due = dayNumber(i.dueDate);
        return due == null || due <= todayNumber(now);
      }).length,
    0
  );

  const blockingOwners = new Set(blocked.map((s) => blockingParty(s.row)?.owner).filter(Boolean));

  return {
    inProgress: live.length,
    blocked: blocked.length,
    blockedOwners: [...blockingOwners],
    startingThisWeek,
    stepsOutstanding,
    stepsDue,
    complete: rows.length - live.length,
  };
};

/** Chip counts, matching the states the table shows. */
export const stateCounts = (rows = [], now) => {
  const out = { inProgress: 0, blocked: 0, starting: 0, complete: 0 };
  rows.forEach((r) => {
    const k = joinerState(r, now).key;
    if (k === "complete") out.complete += 1;
    else if (k === "blocked") out.blocked += 1;
    else if (k === "starting") out.starting += 1;
    else out.inProgress += 1;
  });
  return out;
};

export const BUCKETS = {
  all: () => true,
  inProgress: (s) => !["complete", "blocked", "starting"].includes(s.key),
  blocked: (s) => s.key === "blocked",
  starting: (s) => s.key === "starting",
  complete: (s) => s.key === "complete",
};

/**
 * Sorted by start date, not by name: "a joiner three days out matters more than
 * one three weeks out". Those with no start date sort last — they cannot be
 * placed on the runway.
 */
export const sortByStart = (rows = []) =>
  [...rows].sort((a, b) => {
    const x = dayNumber(a?.employee?.joiningDate);
    const y = dayNumber(b?.employee?.joiningDate);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return x - y;
  });

/**
 * Where the cohort is stuck: the standard steps, across every live joiner.
 *
 * Keyed on the label, because that is what makes a step "the same step" for two
 * people whose checklists are separate copies of one template.
 */
export const stepBreakdown = (rows = []) => {
  const live = rows.filter((r) => !r?.progress?.complete);
  const byLabel = new Map();
  live.forEach((r) => {
    (r.items || []).forEach((i) => {
      const entry = byLabel.get(i.label) || {
        label: i.label,
        owner: i.owner,
        order: i.order ?? 0,
        done: 0,
        outstanding: 0,
        notYet: 0,
      };
      if (i.status === "done" || i.status === "waived") entry.done += 1;
      else if (i.required === false) entry.notYet += 1;
      else entry.outstanding += 1;
      byLabel.set(i.label, entry);
    });
  });
  return [...byLabel.values()]
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
    .map((s) => ({ ...s, total: s.done + s.outstanding + s.notYet }));
};

/** Who owes what, across the live cohort. */
export const owedBy = (rows = []) => {
  const live = rows.filter((r) => !r?.progress?.complete);
  const counts = new Map(OWNERS.map((o) => [o, 0]));
  live.forEach((r) => {
    outstanding(r).forEach((i) => {
      const owner = i.owner || "admin";
      counts.set(owner, (counts.get(owner) || 0) + 1);
    });
  });
  return OWNERS.map((owner) => ({
    owner,
    label: OWNER_LABEL[owner],
    count: counts.get(owner) || 0,
  })).filter((o) => o.count > 0);
};

/**
 * What lands in the next fortnight.
 *
 * Written as a sentence per day rather than a list of rows: "Two joiners start"
 * followed by who, which is how somebody scanning a fortnight actually reads
 * it. A bare list of eleven step names for one date says the same thing and
 * answers nothing.
 */
export const nextDays = (rows = [], days = 14, now) => {
  const today = todayNumber(now);
  const byDay = new Map();
  const bucket = (day) => {
    if (day == null || day < today || day > today + days) return null;
    if (!byDay.has(day)) byDay.set(day, { starts: [], due: [] });
    return byDay.get(day);
  };

  rows.filter((r) => !r?.progress?.complete).forEach((r) => {
    const name = `${r.employee?.fname || ""} ${r.employee?.lname || ""}`.trim() || "A joiner";
    bucket(dayNumber(r.employee?.joiningDate))?.starts.push(name);
    outstanding(r).forEach((i) => bucket(dayNumber(i.dueDate))?.due.push({ name, label: i.label }));
  });

  const list = (names) =>
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

  return [...byDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([day, { starts, due }]) => {
      const parts = [];
      if (starts.length) {
        parts.push({
          lead: starts.length === 1 ? "One joiner starts." : `${starts.length} joiners start.`,
          detail: list(starts),
        });
      }
      if (due.length) {
        const people = [...new Set(due.map((d) => d.name))];
        parts.push({
          lead: due.length === 1 ? "One step falls due." : `${due.length} steps fall due.`,
          detail:
            due.length <= 3
              ? due.map((d) => `${d.label} · ${d.name}`).join("; ")
              : `Across ${list(people)}.`,
        });
      }
      return { day, date: new Date(day * DAY_MS), parts };
    });
};
